/**
 * AuthProvider — validates session on mount and populates @ken/auth store.
 *
 * Per KEN_LAYER_7 §7.1 + §8.1:
 *   - On mount: fetches /api/shell/auth/session (browser sends HttpOnly cookie).
 *   - On 401: marks unauthenticated; route guard redirects to /auth/login.
 *   - On 412 Precondition Failed: pre-tenant session — redirect to tenant select.
 *   - On success: populates the store with user + tenant + permissions.
 *   - Polls every 60s to catch permissions_version changes when SSE is down
 *     (KEN_LAYER_7 §8.1 step 5).
 */

import { useAuthStore } from "@ken/auth";
import { useEffect, type ReactElement, type ReactNode } from "react";

interface SessionResponse {
  user: {
    ken_user_id: string;
    email: string;
    display_name: string | null;
    roles: string[];
    /** 'member' | 'advisor' — multi_company_access (ADR-0089/0090). */
    membership_type?: string;
  };
  tenant: {
    id: string;
    slug: string;
    name: string;
    vertical: string;
    enabled_modules: string[];
    branding: Record<string, unknown>;
    feature_flags: Record<string, boolean>;
    permissions_version: number;
    manifests_version: number;
    /** ISO 4217 currency for the active tenant; read into TenantContext. */
    currency?: string;
  } | null;
  permissions: string[];
  permissions_version: number;
  /** Whether the signed-in identity has TOTP two-factor enabled
   *  (mfa_two_factor). Identity-level; holds across tenant switches. */
  mfa_enabled?: boolean;
}

const SESSION_URL = "/api/shell/auth/session";

async function fetchSession(): Promise<SessionResponse | null | "pre-tenant"> {
  const res = await fetch(SESSION_URL, { credentials: "include" });
  if (res.status === 401) return null;
  if (res.status === 412) return "pre-tenant";
  if (!res.ok) throw new Error(`session fetch failed: ${res.status}`);
  return (await res.json()) as SessionResponse;
}

export function AuthProvider({ children }: { children: ReactNode }): ReactElement {
  const setLoading = useAuthStore((s) => s.setLoading);
  const setSession = useAuthStore((s) => s.setSession);
  const setUnauthenticated = useAuthStore((s) => s.setUnauthenticated);

  useEffect(() => {
    let cancelled = false;

    const refresh = async (): Promise<void> => {
      try {
        const result = await fetchSession();
        if (cancelled) return;

        if (result === null) {
          setUnauthenticated();
          return;
        }
        if (result === "pre-tenant") {
          setUnauthenticated("pre-tenant");
          return;
        }

        // membership_type is optional on UserIdentity (exactOptionalProperty-
        // Types): only include the key when the server sent a recognised
        // value, so we never assign an explicit `undefined`.
        const membershipType =
          result.user.membership_type === "advisor" ? "advisor" : "member";
        setSession({
          user: {
            ken_user_id: result.user.ken_user_id,
            email: result.user.email,
            display_name: result.user.display_name ?? undefined,
            roles: result.user.roles,
            membership_type: membershipType,
          },
          tenantId: result.tenant?.id,
          permissions: new Set(result.permissions),
          permissions_version: result.permissions_version,
          enabledModules: result.tenant?.enabled_modules ?? [],
          tenant: {
            currency: result.tenant?.currency ?? "CAD",
            // `name` is optional (exactOptionalPropertyTypes): only set the
            // key when the server actually resolved a tenant name.
            ...(result.tenant?.name !== undefined
              ? { name: result.tenant.name }
              : {}),
          },
          // mfa_enabled is optional (exactOptionalPropertyTypes): only set the
          // key when the server actually sent a boolean.
          ...(typeof result.mfa_enabled === "boolean"
            ? { mfa_enabled: result.mfa_enabled }
            : {}),
        });
      } catch (err) {
        if (cancelled) return;
        setUnauthenticated(err instanceof Error ? err.message : "session error");
      }
    };

    setLoading();
    void refresh();
    // Phase L: removed the 60s polling. SSE is the live channel
    // (ADR-039); duplicate polling is wasteful and was flagged as a
    // duplicate-source-of-truth in the 2026-05-14 audit. The
    // permissions_version-bumped SSE event is the trigger that should
    // cause a session refresh — wire that in when the SSE event class
    // lands.

    return () => {
      cancelled = true;
    };
  }, [setLoading, setSession, setUnauthenticated]);

  return <>{children}</>;
}
